import {
  Collapse,
  colors,
  HStack,
  Table,
  TableCaption,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VisuallyHidden,
} from "@biblioteksentralen/react";
import { Weekday } from "@libry-content/common";
import { capitalize } from "radash";
import { ComponentProps, Fragment, ReactNode, useState } from "react";
import { getSelfServiceLabel } from "../../utils/getSelfServiceLabel";
import { useTranslation } from "../../utils/hooks/useTranslation";
import HideServerSide from "../HideServerSide";
import { useCommonData } from "../layout/CommonDataProvider";
import { DivergentOpeningHoursAlert } from "./DivergentOpeninghoursAlert";
import { DivergentOpeninghoursIcon } from "./DivergentOpeninghoursIcon";
import { OpeningHoursForDate, OpeningHoursHelper } from "./openingHoursHelper/openingHoursHelper";
import { TimeSpans } from "./TimeSpan";

interface Props {
  openingHoursHelper: OpeningHoursHelper;
}

const TableRow = ({ border, ...rest }: ComponentProps<typeof Tr> & { border?: string }) => (
  <Tr sx={border ? { td: { border } } : undefined} {...rest} />
);

export const StyledTable: typeof Table = (props) => (
  <Table
    sx={{
      th: {
        color: "inherit",
        textTransform: "none",
        border: "none",
      },
      "td,th": {
        maxWidth: "10em",
        padding: "0.75em 0.5em",
        verticalAlign: "top",
        borderColor: colors.grey15,

        "&:not(:first-of-type)": {
          textAlign: "end",
        },
      },
      "tr:last-child": {
        td: {
          border: "none",
        },
      },
    }}
    {...props}
  />
);

type ThProps = ComponentProps<typeof Th>;

export const TableHeaderCell = ({ children, ...thProps }: ThProps & { children?: ReactNode }) => (
  <Th fontSize="md" fontWeight="semibold" letterSpacing="tight" {...thProps}>
    {children}
  </Th>
);

// Week of opening hours. Today and the rest of the week has tooltips with this week's
// special opening hours; earlier days in the with next week's special opening hours
function OpeningHoursTable({ openingHoursHelper }: Props) {
  const translate = useTranslation();
  const { t, lang } = translate;
  const { site } = useCommonData();
  const hasSelfService = openingHoursHelper.hasSelfService;
  const [showSpecialHours, setShowSpecialHours] = useState<Weekday | undefined>(
    // Viser førstkommende dag med avvikende åpningstider for å vise brukeren at det er mer info å hente ved klikk på dager med avvik
    openingHoursHelper.sortedWeekOpeningHours(translate).find((day) => day.divergentHoursForDate)?.weekday
  );

  return (
    <HideServerSide>
      {/* HideServerSide makes sure we have `data-nosnippet` on the server side but to avoid outdated snippets in
     search results we have to add it to the client side too (crawlers do run some js) */}
      <StyledTable data-nosnippet>
        <TableCaption>
          <VisuallyHidden>{t("Åpningstider")}</VisuallyHidden>
        </TableCaption>
        <Thead>
          <Tr>
            <TableHeaderCell>
              <VisuallyHidden>{t("Dag")}</VisuallyHidden>
            </TableHeaderCell>
            {!hasSelfService && (
              <TableHeaderCell>
                <VisuallyHidden>{t("Åpningstider")}</VisuallyHidden>
              </TableHeaderCell>
            )}
            {hasSelfService && (
              <>
                <TableHeaderCell>{t("Betjent")}</TableHeaderCell>
                <TableHeaderCell>{capitalize(getSelfServiceLabel(lang, site))}</TableHeaderCell>
              </>
            )}
          </Tr>
        </Thead>
        <Tbody>
          {openingHoursHelper.sortedWeekOpeningHours(translate).map((openingHoursForDate) => {
            // Don't warn about special hours if they equal the normal ones
            const hasNormalSpecialHours = openingHoursForDate.normalHours?.isSpecial;
            const hasSelfServiceSpecialHours = openingHoursForDate.selfService?.isSpecial;
            const hasSpecialHours = hasNormalSpecialHours || hasSelfServiceSpecialHours;

            const showingSpecialHours = showSpecialHours === openingHoursForDate.weekday;
            const onClick = hasSpecialHours
              ? () => setShowSpecialHours(showingSpecialHours ? undefined : openingHoursForDate.weekday)
              : undefined;

            return (
              <Fragment key={openingHoursForDate.label}>
                <TableRow
                  fontWeight={openingHoursForDate.isToday ? 600 : "normal"}
                  onClick={onClick}
                  cursor={hasSpecialHours ? "pointer" : undefined}
                  border={hasSpecialHours ? "none" : undefined}
                >
                  <Td>
                    <Text whiteSpace="nowrap">{openingHoursForDate.label}</Text>
                  </Td>
                  <Td color={hasNormalSpecialHours ? colors.statusYellow : undefined}>
                    <HStack justify="end">
                      {hasNormalSpecialHours && <DivergentOpeninghoursIcon />}
                      <span>
                        {openingHoursForDate.normalHours?.closed ? (
                          t("Stengt")
                        ) : (
                          <TimeSpans spans={openingHoursForDate.normalHours?.spans} />
                        )}
                      </span>
                    </HStack>
                  </Td>
                  {hasSelfService && (
                    <Td color={hasSelfServiceSpecialHours ? colors.statusYellow : undefined}>
                      <HStack justify="end">
                        {hasSelfServiceSpecialHours && <DivergentOpeninghoursIcon />}
                        <span>
                          {openingHoursForDate.selfService?.enabled ? (
                            <TimeSpans spans={openingHoursForDate.selfService.spans} />
                          ) : (
                            t("Stengt")
                          )}
                        </span>
                      </HStack>
                    </Td>
                  )}
                </TableRow>
                {hasSpecialHours && !!site && (
                  <SpecialHoursDetails
                    date={openingHoursForDate.date}
                    show={showingSpecialHours}
                    openingHoursForDate={openingHoursForDate}
                    close={() => setShowSpecialHours(undefined)}
                  />
                )}
              </Fragment>
            );
          })}
        </Tbody>
      </StyledTable>
    </HideServerSide>
  );
}

type SpecialHoursDetailsProps = {
  close: () => void;
  show: boolean;
  openingHoursForDate: OpeningHoursForDate;
  date: Date;
};

const SpecialHoursDetails = (props: SpecialHoursDetailsProps) => (
  <Tr onClick={props.close} cursor="pointer">
    <Td colSpan={3} padding="0 !important">
      <Collapse in={props.show} animateOpacity>
        <DivergentOpeningHoursAlert
          date={props.date}
          marginBottom=".5rem"
          openingHoursForDate={props.openingHoursForDate}
        />
      </Collapse>
    </Td>
  </Tr>
);

export default OpeningHoursTable;
